import { useCallback, useMemo, useState } from "react";
import { useContentConversion } from "src/components/WysiwygEditor/lexical/hooks/useContentConversion";
import { useHTMLConfig } from "src/components/WysiwygEditor/lexical/hooks/useHTMLConfig";
import AllNodes from "src/components/WysiwygEditor/lexical/nodes/AllNodes";
import { createHeadlessEditor } from "src/components/WysiwygEditor/lexical/utils/headlessEditor";
import {
  polishMessage,
  type MessageContextSchema,
} from "src/generated/hooks/avela-ai/requests";
import { useAbortController } from "src/hooks/useAbortController";
import { usePrompt } from "src/hooks/usePrompt";
import { PolishPreviewDrawer } from "../components/PolishPreviewDrawer";
import {
  PolishProgressDrawer,
  usePolishProgress,
} from "../components/PolishProgressDrawer";
import { MessageContent, MessageFormType } from "../schemas";

export function useMessagePolish(
  hasRecipientsWithEmail: boolean,
  hasRecipientsWithPhone: boolean,
  messageContext: MessageContextSchema | undefined
) {
  const [isPolishing, setIsPolishing] = useState(false);

  const htmlConfig = useHTMLConfig({
    variableMode: "collapsed",
    enableVariables: true,
  });

  const converter = useContentConversion({
    variableMode: "collapsed",
    enableVariables: true,
  });

  const headlessEditor = useMemo(
    () =>
      createHeadlessEditor({
        nodes: AllNodes,
        html: htmlConfig,
        theme: {},
      }),
    [htmlConfig]
  );

  const polishProgress = usePolishProgress();
  const {
    prompt: confirmPolish,
    resolve: resolvePolish,
    promptProps: polishPromptProps,
  } = usePrompt<MessageContent, MessageContent>();

  const {
    createController,
    cancel: abortCancel,
    isAborted,
    clearRef,
  } = useAbortController();

  const cancelPolish = useCallback(() => {
    abortCancel(polishProgress.cancel);
  }, [abortCancel, polishProgress.cancel]);

  const handlePolish = useCallback(
    async (values: MessageFormType) => {
      const controller = createController();

      try {
        setIsPolishing(true);
        polishProgress.onOpen();
        polishProgress.onPreparations();

        const originalMessage = values.messages.find(
          (message) => message.language === "en"
        );
        if (!originalMessage) {
          throw new Error("Original message is undefined");
        }
        const originalEmailLexical = converter.parse(
          originalMessage.email_lexical
        );

        const { html, attachments } = converter.serialized.toHtmlWithCIDs(
          headlessEditor,
          originalEmailLexical
        );

        const originalSmsLexical = converter.parse(originalMessage.sms_lexical);
        const smsText = converter.serialized.toPlainText(
          headlessEditor,
          originalSmsLexical
        );

        polishProgress.onPolishing();
        const response = await polishMessage({
          body: {
            subject: originalMessage.subject,
            email: html,
            sms: smsText,
            // TODO: The message_context parameter is not yet supported by the
            // backend, but will be required for triggered messages.
            // message_context: messageContext,
          },
          signal: controller.signal,
        });

        if (!response.data) {
          throw new Error("Polish response is empty");
        }

        polishProgress.onFinishing();

        const polishedSubject = hasRecipientsWithEmail
          ? response.data.polished.subject ?? ""
          : undefined;
        const polishedEmailLexical =
          hasRecipientsWithEmail && response.data.polished.email
            ? converter.restoreCIDs(
                converter.serialized.fromHtml(
                  headlessEditor,
                  response.data.polished.email
                ),
                attachments
              )
            : undefined;

        const polishedSmsLexical =
          hasRecipientsWithPhone && response.data.polished.sms
            ? converter.serialized.fromPlainText(
                headlessEditor,
                response.data.polished.sms
              )
            : undefined;

        const polishedMessage = {
          subject: polishedSubject,
          email_lexical: polishedEmailLexical,
          sms_lexical: polishedSmsLexical,
          language: "en",
          language_name: "English",
        } satisfies MessageContent;

        polishProgress.onCompleted();
        polishProgress.onClose();

        return await confirmPolish(polishedMessage);
      } catch (error) {
        if (isAborted(controller)) {
          return undefined;
        }
        throw error;
      } finally {
        clearRef();
        setIsPolishing(false);
        polishProgress.onClose();
      }
    },
    [
      hasRecipientsWithEmail,
      hasRecipientsWithPhone,
      converter,
      headlessEditor,
      confirmPolish,
      polishProgress,
      createController,
      isAborted,
      clearRef,
    ]
  );

  return {
    isPolishing,
    progressDrawer: (
      <PolishProgressDrawer
        isOpen={polishProgress.isOpen}
        onClose={cancelPolish}
        currentStep={polishProgress.currentStep}
      />
    ),
    previewDrawer: (
      <PolishPreviewDrawer
        isOpen={!!polishPromptProps}
        polishedMessage={
          polishPromptProps ?? {
            language: "en",
            language_name: "English",
          }
        }
        hasRecipientsWithEmail={hasRecipientsWithEmail}
        hasRecipientsWithPhone={hasRecipientsWithPhone}
        onConfirm={resolvePolish}
        onCancel={() => resolvePolish(null)}
        messageContext={messageContext}
      />
    ),
    handlePolish,
  };
}
